import LinkSimpleIcon from 'assets/icons/LinkSimpleIcon'
import When from 'components/when/When'
// Removed empty import from 'react-router-dom'
import hasValue from 'utils/hasValue'
import {ITreatmentPlan} from 'screens/Patients/LeadsProfile/main/treatment/types/treatmentPlan.types'
import {SectionTitle} from '../../ProductionReview/ProductionSetupReview'
import {AllFiles} from 'screens/Patients/LeadsProfile/main/files/types/files.types'
import {bytesToMB} from '@utils/bytesToMB'
import {fileFormatDateTime} from 'utils/DateFunctions'
import CommonSVG from 'components/atom/SVG/CommonSVG'
import {SVG_ZIP_FILE} from 'utils/SvgConstants'
import {useContext} from 'react'
import useDispatchAction from '@hooks/useDispatchAction'
import {AuthContext} from 'context/AuthContext'
import userTypes from '@constants/userTypes'
import {downloadFile} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileFiles.slice'
import {downloadBlob} from 'utils/download'
import {safeParseInt} from 'utils/ConstFunctions'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import {Spin} from 'antd'
import Spinner from 'components/spinner/Spinner'
import JSZip from 'jszip'
import AntdMessage from 'components/modal/Alert/AntdMessage'
import SuccessToast from 'components/modal/Alert/SuccessToast'

const STLFilesView = ({
  showTitle = true,
  treatmentPlan,
  stlFileMetaData,
}: {
  showTitle?: boolean
  treatmentPlan: ITreatmentPlan
  stlFileMetaData: AllFiles[]
}) => {
  const {dispatchAction} = useDispatchAction()
  const {userId} = useContext(AuthContext)
  const hasFiles = hasValue(stlFileMetaData)
  const hasLinks = hasValue(treatmentPlan?.stl_file_metadata?.link)
  const hasAny = hasFiles || hasLinks
  const {downloadingFile} = useSelector((state: RootState) => state.leadsProfileFiles)

  // 500 MB threshold
  const SIZE_LIMIT_BYTES = 200 * 1024 * 1024

  const getTotalSizeBytes = (files: AllFiles[]) => files.reduce((sum, f) => sum + (f.size ?? 0), 0)

  const downloadFileViaHref = (file: AllFiles) => {
    const href = (file as any).download_url || file.url
    if (!href) return
    const anchor = document.createElement('a')
    anchor.href = href
    anchor.download = file.name || `file-${file.file_id}`
    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }

  const handleDownloadViaHref = (files: AllFiles[]) => {
    const downloadable = files.filter((f) => !!((f as any).download_url || f.url))
    if (!downloadable.length) {
      AntdMessage({type: 'error', text: 'No downloadable URLs found for these files'})
      return
    }
    downloadable.forEach((file, idx) => setTimeout(() => downloadFileViaHref(file), idx * 300))
  }

  const handleDownloadViaApi = async (files: AllFiles[]) => {
    if (!userId) {
      AntdMessage({type: 'error', text: 'Unable to download files. Missing user context.'})
      return
    }
    const zip = new JSZip()
    let count = 0
    for (const file of files) {
      try {
        if ((file as any).is_gdrive_platform && (((file as any).download_url) || file.url)) {
          // fallback to href for drive files
          downloadFileViaHref(file)
          continue
        }
        if (file?.file_id) {
          const response = await dispatchAction(
            downloadFile({requester_user_id: safeParseInt(userId), requester_user_type: userTypes.DOCTOR, file_id: safeParseInt(file.file_id)})
          ).unwrap()
          const blob = new Blob([response], {type: file?.type || 'application/octet-stream'})
          zip.file(file?.name || `file-${file.file_id}.dat`, blob)
          count++
        } else if (file.url) {
          downloadFileViaHref(file)
        }
      } catch (err) {
        console.error(`Error downloading file_id ${file.file_id}`, err)
      }
    }
    if (count > 0) {
      const zipBlob = await zip.generateAsync({type: 'blob'})
      downloadBlob(zipBlob, `STL_Files_${treatmentPlan?.treatment_plan_id || 'files'}.zip`)
      SuccessToast('STL files downloaded successfully')
    }
  }

  const handleDownloadAll = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!hasFiles) return
    const total = getTotalSizeBytes(stlFileMetaData)
    if (total >= SIZE_LIMIT_BYTES) {
      handleDownloadViaHref(stlFileMetaData)
    } else {
      await handleDownloadViaApi(stlFileMetaData)
    }
  }

  return (
    <div>
      {showTitle && <SectionTitle title='STL Files' />}

      {hasAny ? (
        <>
          {/* STL files */}
          <When isTrue={hasFiles}>
            <Spin indicator={<Spinner loading={downloadingFile} />} spinning={downloadingFile}>
              <div className='flex items-center gap-3 justify-between border-b border-neutral-200 pb-3 mb-3'>
                <div className='flex flex-col w-full gap-3'>
                  {stlFileMetaData
                    ?.filter((f: AllFiles) => f?.extension?.toLowerCase() === 'zip')
                    .map((f: AllFiles, idx: number) => (
                      <div
                        key={f.file_id ?? idx}
                        className='flex items-center justify-between w-full border border-mediumGray rounded-lg p-3 cursor-pointer'
                        onClick={() => handleDownloadAll()}
                      >
                        <div className='flex items-center gap-3'>
                          <div className='flex items-center justify-center w-12 h-12 bg-secondarySupport rounded-lg border border-mediumGray'>
                            <CommonSVG svg={SVG_ZIP_FILE} width='48' height='48' />
                          </div>
                          <div className='flex flex-col'>
                            <p className='text-base text-black mb-1 break-all'>{f.name}</p>
                            <p className='text-sm text-textColor'>
                              {bytesToMB(f.size)} MB, {fileFormatDateTime(f.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>

              </div>
            </Spin>
          </When>

          {/* Link section */}
          <When isTrue={hasLinks}>
            <div className='flex items-start gap-3'>
              <div className='flex items-center justify-center w-10 h-10 bg-primarySupport rounded-lg'>
                <LinkSimpleIcon />
              </div>
              <div className='flex flex-col'>
                <p className='text-base text-black mb-1'>Link</p>
                {treatmentPlan?.stl_file_metadata?.link?.map((link, index) => (
                  <a
                    key={index}
                    href={link}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-sm font-normal text-primaryColor underline mb-0.5'
                  >
                    {link}
                  </a>
                ))}
              </div>
            </div>
          </When>
        </>
      ) : (
        <div className='flex justify-start '>
          <div className='text-textColor text-sm'>{'No files added'}</div>
        </div>
      )}
    </div>
  )
}

export default STLFilesView
